import { Injectable, inject } from '@angular/core';
import type { TODO } from '@b2b/types/core';

import { AuthenticationService } from '../auth/authentication.service';

export type NavigationEngine = 'permissions' | 'roles';

export interface NavigationSecurity {
  engine?: NavigationEngine;
  features?: TODO;
  permissions?: TODO;
  roles?: TODO;
}

export interface NavigationItem {
  name: string;
  title?: string;
  sref?: string;
  href?: string;
  roles?: TODO;
  permissions?: TODO;
  features?: TODO;
  items?: NavigationItem[];
  nested?: TODO[];
  toggle?: boolean;
  [extra: string]: TODO;
}

export type NavigationItems = Record<string, NavigationItem[]>;

/**
 * NavigationService (Angular 18).
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/navigation/navigation.service.ts
 *
 * Exposed back to AngularJS via `downgradeInjectable` under the legacy name
 * `'NavigationService'`, so existing consumers in b2b-ui keep working
 * without modification (see ng-downgraded-components.ts).
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  isShown = true;
  securityEnhanced = false;
  currentStateName = '';
  foundStateName = '';
  security: NavigationSecurity = {};
  items: NavigationItems = {} as NavigationItems;
  current: NavigationItem[] = [];

  private auth = inject(AuthenticationService);

  init(items?: NavigationItems, isSecure?: boolean, engine?: NavigationEngine | null): boolean {
    if (items) {
      this.items = items;
    }

    this.securityEnhanced = isSecure === true;
    if (this.securityEnhanced) {
      this.security.features = this.auth.getFeatures();

      if (engine === 'permissions') {
        this.security.engine = engine;
        this.security.permissions = this.auth.getPermissions();
        this.filterByEngine();
      } else {
        this.security.engine = 'roles';
        this.security.roles = this.auth.getRoles();
        this.filterByEngine();
      }
    }

    return true;
  }

  reset(): void {
    this.securityEnhanced = false;
    this.security = {};
    this.items = {} as NavigationItems;
    this.current = [];
  }

  toggle(): boolean {
    return (this.isShown = !this.isShown);
  }

  show(): boolean {
    return (this.isShown = true);
  }

  hide(): boolean {
    return (this.isShown = false);
  }

  add(item: NavigationItem, stateName: string): boolean {
    let r = false;
    const allow = this.checkSecurity(item);
    const state = this.findState(stateName);

    if (item && Object.prototype.hasOwnProperty.call(item, 'name') && allow) {
      const _name = item.name;

      if (!state) {
        this.items[stateName] = [];
      }

      if (!this.find(_name, stateName)) {
        this.items[stateName].push(item);
      }

      r = true;
    }

    return r;
  }

  updateItem(updatedItem: TODO, stateName: string): void {
    const itemIndex = this.find(updatedItem.name, stateName, true);

    if (itemIndex !== false) {
      const currentItem = this.items[stateName][itemIndex as number];

      updatedItem.nested.forEach((route: TODO) => {
        currentItem.nested!.push(route);
      });

      updatedItem.permissions.forEach((permission: TODO) => {
        (currentItem.permissions as TODO[]).push(permission);
      });

      updatedItem.items.forEach((item: TODO) => {
        currentItem.items!.push(item);
      });

      this.filterByEngine();
    }
  }

  remove(name: string, stateName: string): void {
    if (Object.prototype.hasOwnProperty.call(this.items, stateName)) {
      for (let i = 0, ii = this.items[stateName].length; i < ii; i++) {
        if (this.items[stateName][i].name === name) {
          this.items[stateName].splice(i, 1);
          break;
        }
      }
    }
  }

  find(
    name: string,
    stateName: string,
    returnIndexOnly?: boolean,
    searchField?: string,
  ): NavigationItem | number | false {
    let result: NavigationItem | number | false = false;
    const field = searchField ? searchField : 'name';

    if (Object.prototype.hasOwnProperty.call(this.items, stateName)) {
      for (let i = 0, ii = this.items[stateName].length; i < ii; i++) {
        if ((this.items[stateName][i] as TODO)[field] === name) {
          if (returnIndexOnly) {
            result = i;
          } else {
            result = this.items[stateName][i];
          }
          break;
        }
      }
    }

    return result;
  }

  deepFind(name: string, stateName: string, searchField?: string): NavigationItem | false {
    let result: NavigationItem | false = false;
    const field = searchField ? searchField : 'name';

    if (Object.prototype.hasOwnProperty.call(this.items, stateName)) {
      for (let i = 0, ii = this.items[stateName].length; i < ii; i++) {
        if ((this.items[stateName][i] as TODO)[field] === name) {
          result = this.items[stateName][i];
          break;
        } else if (Object.prototype.hasOwnProperty.call(this.items[stateName][i], 'items')) {
          const children = this.items[stateName][i].items!;
          for (let j = 0, jj = children.length; j < jj; j++) {
            if ((children[j] as TODO)[field] === name) {
              result = this.items[stateName][i];
              break;
            }
          }
        }
      }
    }

    return result;
  }

  findState(stateName?: string): NavigationItem[] | false {
    let result: NavigationItem[] | false = false;

    if (stateName) {
      if (Object.prototype.hasOwnProperty.call(this.items, stateName)) {
        result = this.items[stateName];
        this.foundStateName = stateName;
      } else {
        for (const item in this.items) {
          if (Object.prototype.hasOwnProperty.call(this.items, item)) {
            const regex = new RegExp('^' + item + '.*');

            if (regex.test(stateName)) {
              result = this.items[item];
              this.foundStateName = item;
              break;
            }
          }
        }
      }
    }

    return result;
  }

  toggleState(stateName: string, currentStateName: string): void {
    if (Object.prototype.hasOwnProperty.call(this.items, currentStateName)) {
      for (let i = 0, ii = this.items[currentStateName].length; i < ii; i++) {
        this.items[currentStateName][i].toggle = false;
      }
    }

    try {
      (this.deepFind(stateName, currentStateName, 'sref') as NavigationItem).toggle = true;
    } catch {
      // State wasn't found
    }
  }

  swap(name: string, stateName: string, item: NavigationItem): boolean {
    const i = this.find(name, stateName, true);
    let r = false;
    let result: NavigationItem[] = [];

    if (i !== false) {
      result = this.items[stateName].splice(i as number, 1, item);
      if (result.length === 1) {
        r = true;
      }
    }
    return r;
  }

  update(stateName?: string): boolean {
    let r = false;
    const state = this.findState(stateName);

    if (state) {
      this.currentStateName = this.foundStateName;
      this.current = state;
      r = true;
    } else {
      this.currentStateName = 'default';
      this.current = (this.findState('default') as NavigationItem[]) || [];
    }

    this.toggleState(stateName as string, this.currentStateName);

    return r;
  }

  checkSecurity(item: { roles?: TODO; permissions?: TODO }): boolean {
    let allow = false;

    if (this.security.engine === 'roles') {
      allow = this.checkRoles(item.roles);
    } else {
      allow = this.checkPermissions(item.permissions);
    }

    return allow;
  }

  checkRoles(roles: string | string[] | undefined): boolean {
    let _roles: TODO[] = [];
    let allow = false;

    if (this.securityEnhanced) {
      if (Array.isArray(roles)) {
        _roles = roles;
      } else {
        _roles = ['user'];
      }

      for (let i = 0, ii = _roles.length; i < ii; i++) {
        if ((this.security.roles as TODO[]).indexOf(_roles[i]) !== -1) {
          allow = true;
          break;
        }
      }
    } else {
      allow = true;
    }

    return allow;
  }

  checkPermissions(permissions: TODO): boolean {
    let _permissions: TODO[] = [];
    let allow = false;

    if (this.securityEnhanced) {
      if (Array.isArray(permissions)) {
        _permissions = permissions;
      }

      for (let i = 0, ii = _permissions.length; i < ii; i++) {
        if (
          Object.prototype.hasOwnProperty.call(this.security.permissions, _permissions[i]) ||
          _permissions[i] === '*'
        ) {
          allow = true;
          break;
        }
      }
    } else {
      allow = true;
    }

    return allow;
  }

  checkFeatures(features: TODO): boolean {
    let _features: TODO[] = [];
    let allow = false;

    if (this.securityEnhanced) {
      if (Array.isArray(features)) {
        _features = features;
      } else {
        allow = true;
      }

      for (let i = 0, ii = _features.length; i < ii; i++) {
        if (
          (this.security.features as TODO[]).indexOf(_features[i]) > -1 ||
          _features[i] === '*'
        ) {
          allow = true;
          break;
        }
      }
    } else {
      allow = true;
    }

    return allow;
  }

  filterByEngine(): void {
    for (const item in this.items) {
      if (Object.prototype.hasOwnProperty.call(this.items, item)) {
        this.items[item] = this.filterItems(this.items[item]);
      }
    }
  }

  filterItems(items: NavigationItem[]): NavigationItem[] {
    if (Array.isArray(items)) {
      const filtered: NavigationItem[] = [];

      for (let i = 0, ii = items.length; i < ii; i++) {
        const currentItem = items[i];

        if (!this.checkFeatures(currentItem.features)) {
          continue;
        }

        if (this.security.engine === 'permissions') {
          if (this.checkPermissions(currentItem.permissions)) {
            filtered.push(currentItem);
          }
        } else if (this.security.engine === 'roles') {
          if (this.checkRoles(currentItem.roles)) {
            filtered.push(currentItem);
          }
        }

        if (Object.prototype.hasOwnProperty.call(currentItem, 'items') && Array.isArray(currentItem.items)) {
          currentItem.items = this.filterItems(currentItem.items);
        }
      }
      items = filtered;
    }

    return items;
  }
}
